/**
 * Mes contrats — consultation et signature électronique.
 *
 * La signature se fait ICI, en place : ni nouvelle route, ni nouvelle page. L'ordre des
 * gestes suit l'ordre juridique — lire le document, recevoir un code à usage unique,
 * déclarer sa qualité et son pouvoir d'engager la société, approuver, signer.
 *
 * Aucun document ne transite par une URL publique : tout passe par des appels
 * authentifiés qui renvoient les octets.
 */
import { useEffect, useRef, useState } from "react";
import { Download, FileSignature, Paperclip } from "lucide-react";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { ListCapNotice } from "../components/ui/ListCapNotice";
import { PageHeader } from "../components/ui/PageHeader";
import { PortalState } from "../components/ui/PortalState";
import { apiGet, apiPost, downloadBlob, PortalApiError } from "../api/portal-client";
import { usePortalResource } from "../hooks/usePortalResource";
import { phCapture } from "../lib/posthog";

interface ContractEntry {
  id: string;
  titre: string;
  type: string;
  statut: "draft" | "sent" | "signing" | "signed" | "cancelled";
  statutLabel: string;
  description: string | null;
  sentAt: string | null;
  signedAt: string | null;
  createdAt: string;
  signataireNom: string | null;
  signataireEmailMasque: string | null;
  signataireQualite: string | null;
  sha256Pdf: string | null;
  pdfDisponible: boolean;
  signable: boolean;
  annexes: { kbis: boolean; rib: boolean };
}

const COULEUR_STATUT: Record<ContractEntry["statut"], string> = {
  draft: "bg-border/40 text-muted",
  sent: "bg-terracotta/10 text-terracotta",
  signing: "bg-terracotta/10 text-terracotta",
  signed: "bg-success/10 text-success",
  cancelled: "bg-border/40 text-muted",
};

function messageErreur(e: unknown): string {
  return e instanceof PortalApiError ? e.message : "Une erreur est survenue.";
}

/** Plafond serveur de `/me/contracts` (portal-auth.routes.ts). */
const CONTRATS_CAP = 100;

export function ContractsPage() {
  // W7-B — « Aucun contrat pour l'instant » tombait dès la fin du chargement,
  // coupure réseau et 403 compris : un client pouvait conclure qu'aucun contrat
  // ne l'attendait alors qu'un contrat À SIGNER dormait de l'autre côté.
  const { data, status, error, reload } = usePortalResource<ContractEntry[]>(() =>
    apiGet<ContractEntry[]>("/me/contracts/etat"),
  );
  const [ouvert, setOuvert] = useState<string | null>(null);

  const list = data ?? [];

  async function telecharger(c: ContractEntry) {
    const suffixe = c.statut === "signed" ? "signe" : "a-signer";
    await downloadBlob(`/me/contracts/${c.id}/pdf`, `${c.titre}-${suffixe}.pdf`);
  }

  async function telechargerAnnexe(c: ContractEntry, type: "kbis" | "rib") {
    await downloadBlob(`/me/contracts/${c.id}/annexes/${type}`, `${type}-${c.titre}.pdf`);
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-6">
      <PageHeader
        title="Mes contrats"
        subtitle="Consultez vos contrats et signez ceux qui vous attendent."
      />

      <PortalState status={status} label="vos contrats" error={error} onRetry={reload} />

      {status === "ready" && list.length === 0 && (
        <EmptyState
          icon={FileSignature}
          title="Aucun contrat pour l'instant"
          description="Les contrats que votre fournisseur vous adresse apparaîtront ici, prêts à signer."
          action={{ to: "/messages", label: "Écrire à mon fournisseur" }}
        />
      )}

      {status === "ready" && list.length > 0 && (
        <>
          <div className="space-y-4">
          {list.map((c) => (
            <article key={c.id} className="bg-card border border-border rounded-xl2 p-4">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-ink">{c.titre}</p>
                  {c.description && <p className="text-xs text-muted mt-0.5">{c.description}</p>}
                  <span className={`inline-block mt-2 rounded-full px-2 py-0.5 text-xs font-semibold ${COULEUR_STATUT[c.statut]}`}>
                    {c.statutLabel}
                  </span>
                  {c.sha256Pdf && (
                    <p className="mt-2 text-xs font-mono text-muted break-all">
                      Empreinte : {c.sha256Pdf.slice(0, 32)}…
                    </p>
                  )}
                </div>
                {c.pdfDisponible && (
                  <button
                    onClick={() => telecharger(c)}
                    aria-label={`Télécharger ${c.titre}`}
                    className="shrink-0 p-2 rounded-lg text-terracotta hover:bg-terracotta/10"
                  >
                    <Download size={18} />
                  </button>
                )}
              </div>

              {(c.annexes.kbis || c.annexes.rib) && (
                <div className="mt-3 flex flex-wrap gap-3">
                  {c.annexes.kbis && (
                    <button
                      onClick={() => telechargerAnnexe(c, "kbis")}
                      className="inline-flex items-center gap-1 text-xs text-muted hover:text-terracotta"
                    >
                      <Paperclip size={14} /> Extrait Kbis
                    </button>
                  )}
                  {c.annexes.rib && (
                    <button
                      onClick={() => telechargerAnnexe(c, "rib")}
                      className="inline-flex items-center gap-1 text-xs text-muted hover:text-terracotta"
                    >
                      <Paperclip size={14} /> RIB
                    </button>
                  )}
                </div>
              )}

              {c.signable && (
                <div className="mt-4">
                  {ouvert === c.id ? (
                    <PanneauSignature
                      contrat={c}
                      onAnnuler={() => setOuvert(null)}
                      onSigne={async () => {
                        setOuvert(null);
                        reload();
                      }}
                    />
                  ) : (
                    <Button
                      onClick={() => {
                        setOuvert(c.id);
                        phCapture("portal_cta_clic", { cta: "ouvrir_signature_contrat" });
                      }}
                    >
                      <FileSignature size={16} /> Lire et signer
                    </Button>
                  )}
                </div>
              )}
            </article>
          ))}
          </div>
          <ListCapNotice shown={list.length} cap={CONTRATS_CAP} label="contrats" genre="m" />
        </>
      )}
    </main>
  );
}

// ─── Panneau de signature ─────────────────────────────────────────────────────

interface PanneauProps {
  contrat: ContractEntry;
  onAnnuler: () => void;
  onSigne: () => Promise<void>;
}

function PanneauSignature({ contrat, onAnnuler, onSigne }: PanneauProps) {
  const padRef = useRef<SignatureCanvas | null>(null);
  const [apercu, setApercu] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [nom, setNom] = useState(contrat.signataireNom ?? "");
  const [qualite, setQualite] = useState(contrat.signataireQualite ?? "");
  const [pouvoir, setPouvoir] = useState(false);
  const [luEtApprouve, setLuEtApprouve] = useState(false);
  const [codeEnvoye, setCodeEnvoye] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  // Aperçu du document : les octets arrivent par appel authentifié, jamais par URL
  // publique — on en fabrique une URL locale, révoquée à la fermeture du panneau.
  useEffect(() => {
    let url: string | null = null;
    let annule = false;
    apiGet<Blob>(`/me/contracts/${contrat.id}/pdf`, { asBlob: true })
      .then((blob) => {
        if (annule) return;
        url = URL.createObjectURL(blob);
        setApercu(url);
      })
      .catch(() => setApercu(null));
    return () => {
      annule = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [contrat.id]);

  async function demanderCode() {
    setErreur(null);
    setEnCours(true);
    try {
      const res = await apiPost<{ sent: boolean; emailMasque: string; expiresInMinutes: number }>(
        `/me/contracts/${contrat.id}/otp`,
        {},
      );
      setCodeEnvoye(
        res.sent
          ? `Code envoyé à ${res.emailMasque}. Il est valable ${res.expiresInMinutes} minutes.`
          : "Le code n'a pas pu être envoyé. Contactez votre fournisseur.",
      );
    } catch (e: unknown) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  }

  async function signer() {
    setErreur(null);
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) {
      setErreur("Tracez votre signature dans le cadre.");
      return;
    }
    setEnCours(true);
    try {
      await apiPost(`/me/contracts/${contrat.id}/signer`, {
        otp,
        signatureDataUrl: pad.toDataURL("image/png"),
        nom,
        qualite,
        luEtApprouve,
        pouvoirEngager: pouvoir,
      });
      phCapture("portal_cta_clic", { cta: "signature_contrat" });
      await onSigne();
    } catch (e: unknown) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  }

  const pretASigner =
    otp.trim().length === 6 &&
    nom.trim().length > 0 &&
    qualite.trim().length > 0 &&
    pouvoir &&
    luEtApprouve;

  return (
    <div className="border border-border rounded-xl2 p-4 space-y-4 bg-cream">
      {apercu ? (
        <object data={apercu} type="application/pdf" className="w-full h-72 rounded-lg border border-border">
          <a href={apercu} download={`${contrat.titre}.pdf`} className="text-sm text-terracotta underline">
            Ouvrir le contrat
          </a>
        </object>
      ) : (
        <p className="text-sm text-muted">Chargement du document…</p>
      )}

      <div>
        <Button variant="secondary" onClick={demanderCode} disabled={enCours}>
          Recevoir mon code
        </Button>
        {codeEnvoye && <p className="mt-2 text-xs text-muted">{codeEnvoye}</p>}
      </div>

      <div>
        <label className="block text-sm font-semibold text-ink mb-1" htmlFor={`otp-${contrat.id}`}>
          Code reçu par e-mail
        </label>
        <input
          id={`otp-${contrat.id}`}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          className="w-40 rounded-lg border border-border px-3 py-2 font-mono text-lg tracking-widest"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-semibold text-ink mb-1" htmlFor={`nom-${contrat.id}`}>
            Votre nom
          </label>
          <input
            id={`nom-${contrat.id}`}
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            className="w-full rounded-lg border border-border px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-ink mb-1" htmlFor={`qualite-${contrat.id}`}>
            Votre qualité
          </label>
          <input
            id={`qualite-${contrat.id}`}
            value={qualite}
            onChange={(e) => setQualite(e.target.value)}
            className="w-full rounded-lg border border-border px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-ink mb-1">Votre signature</p>
        <div className="rounded-lg border border-border bg-white">
          <SignatureCanvas
            ref={(instance) => {
              padRef.current = instance;
            }}
            canvasProps={{ className: "w-full h-40 rounded-lg" }}
          />
        </div>
        <button
          type="button"
          onClick={() => padRef.current?.clear()}
          className="mt-1 text-xs text-muted hover:text-terracotta"
        >
          Effacer
        </button>
      </div>

      <label className="flex items-start gap-2 text-sm text-ink">
        <input type="checkbox" checked={pouvoir} onChange={(e) => setPouvoir(e.target.checked)} className="mt-1" />
        <span>Je certifie disposer du pouvoir d'engager la société.</span>
      </label>
      <label className="flex items-start gap-2 text-sm text-ink">
        <input
          type="checkbox"
          checked={luEtApprouve}
          onChange={(e) => setLuEtApprouve(e.target.checked)}
          className="mt-1"
        />
        <span>Lu et approuvé — j'accepte les termes de ce contrat.</span>
      </label>

      {erreur && <p className="text-sm text-danger">{erreur}</p>}

      <div className="flex gap-2">
        <Button onClick={signer} disabled={enCours || !pretASigner}>
          Signer le contrat
        </Button>
        <Button variant="ghost" onClick={onAnnuler} disabled={enCours}>
          Annuler
        </Button>
      </div>
    </div>
  );
}
